import { useSetOrgSalesforceLink } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import { SalesforceLinkFormConflict } from '@/features/SalesforceAccounts/components/LinkConflictPanel'
import {
  SalesforceAccountIdField,
  SalesforceCurrentAccountId,
  SalesforceReasonField,
} from '@/features/SalesforceAccounts/components/SalesforceLinkFields'
import { SalesforceLinkFormDialog } from '@/features/SalesforceAccounts/components/SalesforceLinkFormDialog'
import {
  expectedCurrentFields,
  useSalesforceLinkForm,
} from '@/features/SalesforceAccounts/hooks/useSalesforceLinkForm'
import { isValidSalesforceAccountId } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { Button } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SalesforceLinkFormProps } from './salesforceLinkFormProps'

/**
 * Re-point: the organisation is linked and the operator is naming a different
 * account.
 *
 * Two values are in play and they are treated in opposite ways. The **new**
 * id is a value being created, so it is validated and trimmed. The
 * **current** id is a compare-and-swap target, so it is displayed read-only
 * and sent exactly as the GET returned it: normalising a value you only name
 * for comparison can only make it stop matching.
 *
 * `reason` is required, matching the contract's rule that a rebind needs both
 * a matching token and a non-blank reason.
 */
export function RepointAccountForm({
  orgId,
  currentAccountId,
  onSuccess,
  onReRead,
}: Readonly<SalesforceLinkFormProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [typedId, setTypedId] = useState('')
  const [reason, setReason] = useState('')

  const trimmedId = typedId.trim()
  const isIdValid = isValidSalesforceAccountId(trimmedId)
  const hasReason = reason.trim().length > 0
  // Naming the account it is already on is not a re-point. It is reported as a
  // field error, so it also has to gate submit.
  const isSameAsCurrent = trimmedId.length > 0 && trimmedId === currentAccountId

  const form = useSalesforceLinkForm({
    orgId,
    // Both accounts: the one losing the organisation and the one gaining it.
    accountIds: [trimmedId, ...(currentAccountId ? [currentAccountId] : [])],
    toastId: 'salesforce-repoint-account',
    toastAccountId: trimmedId,
    onSuccess,
    onReRead,
    useLinkMutation: useSetOrgSalesforceLink,
    buildVariables: (token) => ({
      pathParams: { orgId },
      body: {
        salesforceAccountId: trimmedId,
        reason: reason.trim(),
        // Verbatim, whether it is the value the card read or the value the
        // 409 echoed back.
        ...expectedCurrentFields(token),
      },
    }),
    canSubmit: isIdValid && hasReason && !isSameAsCurrent,
  })

  let clientIdError: string | undefined
  if (typedId.length > 0 && !isIdValid) {
    clientIdError = String(
      t('salesforceLink.actions.repoint.validation.format'),
    )
  } else if (isSameAsCurrent) {
    clientIdError = String(
      t('salesforceLink.actions.repoint.validation.sameAsCurrent'),
    )
  }

  return (
    <SalesforceLinkFormDialog
      title={String(t('salesforceLink.actions.repoint.title'))}
      errorTitle={String(t('salesforceLink.actions.repoint.error.title'))}
      errorDescription={form.errorDescription}
      conflictPanel={
        form.isConflict ? (
          <SalesforceLinkFormConflict
            form={form}
            expectedAccountId={currentAccountId}
            variant="repoint"
          />
        ) : undefined
      }
      cancelLabel={String(t('salesforceLink.actions.repoint.cancel'))}
      submitButton={
        <Button
          colorPalette="brand"
          disabled={form.submitDisabled}
          loading={form.isBusy}
          onClick={() => form.submit(currentAccountId)}
        >
          {String(t('salesforceLink.actions.repoint.submit'))}
        </Button>
      }
    >
      <SalesforceCurrentAccountId
        label={String(
          t('salesforceLink.actions.repoint.currentAccountIdLabel'),
        )}
        value={currentAccountId}
      />

      <SalesforceAccountIdField
        label={String(
          t('salesforceLink.actions.repoint.fields.salesforceAccountId.label'),
        )}
        placeholder={String(
          t(
            'salesforceLink.actions.repoint.fields.salesforceAccountId.placeholder',
          ),
        )}
        helper={String(
          t('salesforceLink.actions.repoint.fields.salesforceAccountId.helper'),
        )}
        value={typedId}
        error={form.fieldError ?? clientIdError}
        onChange={(value) => {
          form.clearFieldErrors()
          setTypedId(value)
        }}
      />

      <SalesforceReasonField
        label={String(t('salesforceLink.actions.repoint.fields.reason.label'))}
        placeholder={String(
          t('salesforceLink.actions.repoint.fields.reason.placeholder'),
        )}
        helper={String(
          t('salesforceLink.actions.repoint.fields.reason.helper'),
        )}
        value={reason}
        isRequired
        onChange={(value) => {
          form.clearFieldErrors()
          setReason(value)
        }}
      />
    </SalesforceLinkFormDialog>
  )
}
